// Shared grid-focus utilities. Editable cells in the plan-builder table opt in by
// rendering an element with `data-grid-cell="<id>"`. Ids encode position so that
// vertical navigation can find the same column in an adjacent row.
//
// Id formats:
//   fixed cell:        f-{rowIndex}-{colKey}
//   row week-data:     w-{rowIndex}-{weekIndex}-{field}     // field = 'cpm' | 'investment'
//   bpm input:         b-{field}-{weekIndex}                // field = 'ad' | 'fee' | 'cl'

const SELECTOR = '[data-grid-cell]';

const queryAll = (): HTMLElement[] => Array.from(document.querySelectorAll<HTMLElement>(SELECTOR));

export const focusCellByDelta = (
  current: HTMLElement,
  direction: 'next' | 'prev' | 'down' | 'up'
): void => {
  const all = queryAll();
  if (all.length === 0) return;

  if (direction === 'next' || direction === 'prev') {
    const idx = all.indexOf(current);
    if (idx === -1) return;
    const targetIdx = direction === 'next' ? idx + 1 : idx - 1;
    if (targetIdx < 0 || targetIdx >= all.length) return;
    all[targetIdx].focus();
    return;
  }

  const id = current.getAttribute('data-grid-cell') ?? '';
  const target = findVerticalNeighbor(all, id, direction);
  if (target) {
    target.focus();
  }
};

const findVerticalNeighbor = (
  all: HTMLElement[],
  currentId: string,
  direction: 'up' | 'down'
): HTMLElement | undefined => {
  // Parse the current id and find the input in the row above/below with the same
  // "column" identity. We match on whatever follows the row index.
  const parsed = parseId(currentId);
  if (!parsed) return undefined;
  const step = direction === 'down' ? 1 : -1;

  if (parsed.type === 'fixed') {
    const candidates = all
      .map(el => ({ el, p: parseId(el.getAttribute('data-grid-cell') ?? '') }))
      .filter(c => c.p?.type === 'fixed' && c.p.colKey === parsed.colKey);
    return moveByRow(candidates, parsed.row, step);
  }

  if (parsed.type === 'week') {
    const candidates = all
      .map(el => ({ el, p: parseId(el.getAttribute('data-grid-cell') ?? '') }))
      .filter(
        c =>
          c.p?.type === 'week' && c.p.weekIndex === parsed.weekIndex && c.p.field === parsed.field
      );
    return moveByRow(candidates, parsed.row, step);
  }

  // BPM input rows don't have data rows above/below in the same column-key space,
  // so we leave vertical navigation as a no-op for those.
  return undefined;
};

const moveByRow = (
  candidates: { el: HTMLElement; p: ParsedId | undefined }[],
  fromRow: number,
  step: number
): HTMLElement | undefined => {
  const sorted = candidates
    .filter(c => c.p && 'row' in c.p)
    .sort((a, b) => (a.p as { row: number }).row - (b.p as { row: number }).row);
  const target = sorted.find(c => (c.p as { row: number }).row === fromRow + step);
  return target?.el;
};

type ParsedId =
  | { type: 'fixed'; row: number; colKey: string }
  | { type: 'week'; row: number; weekIndex: number; field: string }
  | { type: 'bpm'; field: string; weekIndex: number };

const parseId = (id: string): ParsedId | undefined => {
  const parts = id.split('-');
  if (parts[0] === 'f' && parts.length >= 3) {
    const row = Number(parts[1]);
    if (Number.isNaN(row)) return undefined;
    return { type: 'fixed', row, colKey: parts.slice(2).join('-') };
  }
  if (parts[0] === 'w' && parts.length === 4) {
    const row = Number(parts[1]);
    const weekIndex = Number(parts[2]);
    if (Number.isNaN(row) || Number.isNaN(weekIndex)) return undefined;
    return { type: 'week', row, weekIndex, field: parts[3] };
  }
  if (parts[0] === 'b' && parts.length === 3) {
    const weekIndex = Number(parts[2]);
    if (Number.isNaN(weekIndex)) return undefined;
    return { type: 'bpm', field: parts[1], weekIndex };
  }
  return undefined;
};

export const buildCellId = {
  fixed: (row: number, colKey: string): string => `f-${row}-${colKey}`,
  week: (row: number, weekIndex: number, field: 'cpm' | 'investment'): string =>
    `w-${row}-${weekIndex}-${field}`,
  bpm: (field: 'ad' | 'fee' | 'cl', weekIndex: number): string => `b-${field}-${weekIndex}`
};
